import { LinkIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import type { ReactNode } from 'react';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import { resolveChannelIcon } from '@/components/bots/Integration/ChannelLogo';

import { CHANNEL_PAGES } from './channelPages';
import Halftone from './Halftone';
import { ArrowGlyph, SectionHeading, tone } from './primitives';

const ROW_PITCH = 56; // chip height (44px) + gap (12px)
const ROWS = 7;
const BOARD_HEIGHT = ROWS * ROW_PITCH - 12;

/** `href` is the channel's own landing page, when it has one. */
type Channel = { key: string; name: string; icon: ReactNode; href?: string };

export default function AgentChannels() {
  const { t } = useLocale('chatbots');

  const chatIcon = (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      className='h-5 w-5 text-primary-600 dark:text-primary-400'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z'
      />
    </svg>
  );

  const left: Channel[] = [
    { key: 'web', name: t('Website chat'), icon: chatIcon },
    {
      key: 'link',
      name: t('Shareable link'),
      icon: (
        <LinkIcon className='h-5 w-5 text-primary-600 dark:text-primary-400' />
      ),
    },
    {
      key: 'wa',
      name: 'WhatsApp',
      icon: resolveChannelIcon('FaWhatsapp', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaWhatsapp,
    },
    {
      key: 'fbm',
      name: 'Messenger',
      icon: resolveChannelIcon('FaFacebookMessenger', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaFacebookMessenger,
    },
    {
      key: 'tg',
      name: 'Telegram',
      icon: resolveChannelIcon('FaTelegram', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaTelegram,
    },
    {
      key: 'line',
      name: 'LINE',
      icon: resolveChannelIcon('FaLine', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaLine,
    },
    {
      key: 'slack',
      name: 'Slack',
      icon: resolveChannelIcon('FaSlack', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaSlack,
    },
  ];

  const right: Channel[] = [
    {
      key: 'gmail',
      name: 'Gmail',
      icon: resolveChannelIcon('Gmail', 'h-5 w-5'),
      href: CHANNEL_PAGES.Gmail,
    },
    {
      key: 'outlook',
      name: 'Outlook',
      icon: resolveChannelIcon('Outlook', 'h-5 w-5'),
      href: CHANNEL_PAGES.Outlook,
    },
    {
      key: 'email',
      name: t('Email forwarding'),
      icon: resolveChannelIcon('FaEnvelope', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaEnvelope,
    },
    {
      key: 'phone',
      name: t('Phone calls'),
      icon: resolveChannelIcon('FaPhone', 'h-[18px] w-[18px]'),
      href: CHANNEL_PAGES.FaPhone,
    },
    {
      key: 'shopify',
      name: 'Shopify',
      icon: resolveChannelIcon('FaShopify', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaShopify,
    },
    {
      key: 'wp',
      name: 'WordPress',
      icon: resolveChannelIcon('FaWordpress', 'h-5 w-5'),
      href: CHANNEL_PAGES.FaWordpress,
    },
    {
      key: 'zapier',
      name: 'Zapier',
      icon: resolveChannelIcon('Zapier', 'h-5 w-5'),
    },
  ];

  return (
    <section id='channels' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          align='center'
          title={t('One agent. Every channel.')}
          tail={t('Same answers, wherever customers find you.')}
        />

        {/* Desktop: a switchboard wired into the agent. */}
        <div
          className='mt-20 hidden items-center lg:grid lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_8rem_minmax(0,1fr)_minmax(0,15rem)]'
          style={{
            height: BOARD_HEIGHT,
            gridTemplateRows: `${BOARD_HEIGHT}px`,
          }}
        >
          <ChannelColumn channels={left} align='end' />
          <Wires side='left' />
          <AgentNode />
          <Wires side='right' />
          <ChannelColumn channels={right} align='start' />
        </div>

        {/* Smaller screens: the agent, then every channel as a grid. */}
        <div className='mt-14 lg:hidden'>
          <div className='flex justify-center pb-16'>
            <AgentNode />
          </div>
          <ul className='mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3'>
            {[...left, ...right].map((channel) => (
              <ChannelChip key={channel.key} channel={channel} />
            ))}
          </ul>
        </div>

        <div
          className={clsxm(
            'mx-auto mt-16 grid max-w-5xl items-center gap-6 rounded-3xl p-6 sm:p-8 lg:mt-28 lg:grid-cols-[1fr_1.35fr] lg:gap-10',
            tone.surface,
            tone.line,
          )}
        >
          <div>
            <p className={clsxm('text-lg font-semibold', tone.ink)}>
              {t('On your website with one line of code')}
            </p>
            <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>
              {t(
                'Paste the snippet and the chat bubble appears in your brand colors. Works with any website, and the REST API covers everything else.',
              )}
            </p>
            <a
              href='https://easy-peasy.ai/docs/agents/channels'
              className='group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
            >
              {t('See every channel in the docs')}
              <ArrowGlyph className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5' />
            </a>
          </div>
          <pre
            dir='ltr'
            className='overflow-x-auto rounded-2xl bg-[#0f0e20] px-5 py-4 text-left font-mono text-[12.5px] leading-6 text-[#c9c9e3] ring-1 ring-inset ring-white/10'
          >
            <code>
              <span className='text-[#7d7da0]'>&lt;</span>
              <span className='text-[#9aa7ff]'>script</span>
              {'\n  '}
              <span className='text-[#7fd6c2]'>src</span>
              <span className='text-[#7d7da0]'>=</span>
              <span className='text-[#f2c98b]'>
                &quot;https://bots.easy-peasy.ai/chat.min.js&quot;
              </span>
              {'\n  '}
              <span className='text-[#7fd6c2]'>data-chat-url</span>
              <span className='text-[#7d7da0]'>=</span>
              <span className='text-[#f2c98b]'>
                &quot;https://bots.easy-peasy.ai/bot/your-agent&quot;
              </span>
              {'\n  '}
              <span className='text-[#7fd6c2]'>defer</span>
              <span className='text-[#7d7da0]'>&gt;&lt;/</span>
              <span className='text-[#9aa7ff]'>script</span>
              <span className='text-[#7d7da0]'>&gt;</span>
            </code>
          </pre>
        </div>
      </div>
    </section>
  );
}

function ChannelChip({
  channel,
  className,
}: {
  channel: Channel;
  className?: string;
}) {
  const chip = clsxm(
    'flex h-full min-h-[2.75rem] w-full min-w-0 items-center gap-3 rounded-full py-1 pe-4 ps-1.5 shadow-[0_1px_2px_rgba(15,15,40,0.04)]',
    tone.surface,
    tone.line,
  );
  const content = (
    <>
      <span
        aria-hidden='true'
        className='flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#f5f5f9] dark:bg-white'
      >
        {channel.icon}
      </span>
      <span
        className={clsxm(
          'text-sm font-medium leading-tight lg:truncate',
          tone.ink,
        )}
      >
        {channel.name}
      </span>
    </>
  );

  return (
    <li className={clsxm('flex min-w-0', className)}>
      {channel.href ? (
        <Link
          href={channel.href}
          className={clsxm(
            chip,
            'transition duration-200 hover:-translate-y-px hover:shadow-[0_12px_28px_-16px_rgba(79,70,229,0.55)] hover:ring-primary-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:hover:ring-primary-400/50',
          )}
        >
          {content}
        </Link>
      ) : (
        <div className={chip}>{content}</div>
      )}
    </li>
  );
}

function ChannelColumn({
  channels,
  align,
}: {
  channels: Channel[];
  align: 'start' | 'end';
}) {
  return (
    <ul
      className={clsxm(
        'flex h-full flex-col gap-3',
        align === 'end' ? 'items-end' : 'items-start',
      )}
    >
      {channels.map((channel) => (
        <ChannelChip
          key={channel.key}
          channel={channel}
          className='h-11 max-w-full'
        />
      ))}
    </ul>
  );
}

/**
 * Curves from every chip in a column to the agent. The board has fixed row
 * heights, so the SVG can be drawn in pixels vertically and stretch freely
 * sideways; strokes keep their width with non-scaling-stroke.
 */
function Wires({ side }: { side: 'left' | 'right' }) {
  const middle = BOARD_HEIGHT / 2;
  const paths = Array.from({ length: ROWS }, (_, index) => {
    const y = index * ROW_PITCH + 22;
    return side === 'left'
      ? `M0 ${y} C 55 ${y}, 45 ${middle}, 100 ${middle}`
      : `M0 ${middle} C 55 ${middle}, 45 ${y}, 100 ${y}`;
  });

  return (
    <svg
      aria-hidden='true'
      viewBox={`0 0 100 ${BOARD_HEIGHT}`}
      preserveAspectRatio='none'
      width='100%'
      height={BOARD_HEIGHT}
      className='block overflow-visible rtl:-scale-x-100'
      style={{ height: BOARD_HEIGHT }}
    >
      {paths.map((d) => (
        <path
          key={`${d}-base`}
          d={d}
          fill='none'
          vectorEffect='non-scaling-stroke'
          className='stroke-[#0f0f28]/[0.12] dark:stroke-white/[0.12]'
          strokeWidth={1}
        />
      ))}
      {paths.map((d, index) => (
        <path
          key={`${d}-signal`}
          d={d}
          fill='none'
          vectorEffect='non-scaling-stroke'
          className={clsxm(
            styles.signal,
            side === 'right' && styles.signalReverse,
            'stroke-primary-500 dark:stroke-primary-400',
          )}
          strokeWidth={2}
          strokeLinecap='round'
          style={{ animationDelay: `${-index * 230}ms` }}
        />
      ))}
    </svg>
  );
}

/** The agent the wires meet at. Its label hangs below the tile so the tile
 * itself stays centred on the wires. */
function AgentNode() {
  const { t } = useLocale('chatbots');

  return (
    <div className='relative flex justify-center'>
      <div className='relative isolate flex h-32 w-32 items-center justify-center overflow-hidden rounded-[32px] bg-[linear-gradient(155deg,rgb(var(--tw-color-primary-500))_0%,rgb(var(--tw-color-primary-700))_100%)] shadow-[0_24px_60px_-20px_rgba(79,70,229,0.7)] ring-1 ring-inset ring-white/15'>
        <Halftone
          at='100% 0%'
          spacing={9}
          maxRadius={3.4}
          className='opacity-40 [--ht:rgb(var(--tw-color-primary-300))]'
        />
        <svg
          viewBox='0 0 24 24'
          fill='none'
          stroke='white'
          strokeWidth='1.6'
          aria-hidden='true'
          className='relative h-12 w-12'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z'
          />
        </svg>
      </div>
      <div className='absolute left-1/2 top-full mt-4 w-56 -translate-x-1/2 text-center'>
        <p className={clsxm('text-base font-semibold', tone.ink)}>
          {t('Your AI agent')}
        </p>
        <p className={clsxm('mt-1 text-sm', tone.quiet)}>
          {t('One knowledge base')}
        </p>
      </div>
    </div>
  );
}
